import { useState } from "react";
import { TableBody as Body, TableCell, TableRow, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import {
  CheckOutlined,
  CloseOutlined,
  InfoOutlined as Info,
} from "@mui/icons-material";

import { HeaderProperties } from "../../../../shared/uiComponents/Table/tableProperties";
import { CustomAlert, Text } from "../../../../shared/uiComponents";
import { InfoRow } from "../../../../components/StyledComponents";

import {
  BillingProperties,
  MBHBillingStatus,
  ModifiedSessionProperties,
} from "../../../../redux/API/ClientAPIHelpers/billingProperties";

export const Headers: Array<HeaderProperties> = [
  { id: "1", name: "Client Name", orderBy: "client.fullName" },
  { id: "2", name: "Session Date", orderBy: "sessionDate" },
  { id: "3", name: "Session Type" },
  { id: "4", name: "Provider" },
  { id: "5", name: "Modified" },
  { id: "6", name: "Submit Date", orderBy: "reportSubmittedAt" },
  { id: "7", name: "Download Date", orderBy: "reportExportedAt" },
  { id: "8", name: "MBH Billing Status", orderBy: "mbhBillingStatus" },
  { id: "9", name: "More Details", width: "50px" },
];

export const ModifiedSessionDetails = ({
  item,
}: {
  item: ModifiedSessionProperties;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const onClose = () => setOpen(false);
  const onOpen = () => setOpen(true);

  return (
    <>
      <Tooltip title={"More information"}>
        <CheckOutlined onClick={onOpen} />
      </Tooltip>
      <CustomAlert
        open={open}
        onClose={onClose}
        title="Change Details"
        Content={() => {
          const {
            originalDate,
            originalEndTime,
            originalStartTime,
            dateModifiedAt,
            dateModifiedBy,
          } = item;
          return (
            <>
              <InfoRow>
                <Text title={"Original Date"} size="smallBold" />
                <Text
                  title={dayjs(originalDate).utc(false).format("MM/DD/YYYY")}
                />
              </InfoRow>
              <InfoRow>
                <Text title={"Original Start Time"} size="smallBold" />
                <Text
                  title={dayjs(originalStartTime).utc(false).format("hh:mm A")}
                />
              </InfoRow>
              <InfoRow>
                <Text title={"Original End Time"} size="smallBold" />
                <Text
                  title={dayjs(originalEndTime).utc(false).format("hh:mm A")}
                />
              </InfoRow>
              <InfoRow>
                <Text title={"Modified by"} size="smallBold" />
                <Text title={dateModifiedBy.fullName} />
              </InfoRow>
              <InfoRow>
                <Text title={"Modified Date"} size="smallBold" />
                <Text
                  title={dayjs(dateModifiedAt).utc(false).format("MM/DD/YYYY")}
                />
              </InfoRow>
            </>
          );
        }}
      />
    </>
  );
};

export const TableBody = ({ data }: { data: Array<BillingProperties> }) => {
  return (
    <Body>
      {data.map((row, index) => (
        <TableRow key={index}>
          <TableCell>{row.client.fullName}</TableCell>
          <TableCell>
            {dayjs.utc(row.sessionDate).format("MM-DD-YYYY")}
          </TableCell>
          <TableCell>{row.sessionType.name}</TableCell>
          <TableCell>{!!row.billedBy ? row.billedBy.fullName : "-"}</TableCell>
          <TableCell>
            {!!row.sessionDateModifiedDetails ? (
              <ModifiedSessionDetails item={row.sessionDateModifiedDetails} />
            ) : (
              <CloseOutlined />
            )}
          </TableCell>
          <TableCell>
            {dayjs(row.reportSubmittedAt).format("MM-DD-YYYY")}
          </TableCell>
          <TableCell>
            {!!row.reportExportedAt
              ? dayjs(row.reportExportedAt).format("MM-DD-YYYY")
              : "Not downloaded"}
          </TableCell>
          <TableCell>
            {
              typeof row.mbhBillingStatus === "number" ?
                MBHBillingStatus[row.mbhBillingStatus] :
                'Not Updated'
            }
          </TableCell>
          <TableCell>
            <DetailedInformation info={row} />
          </TableCell>
        </TableRow>
      ))}
    </Body>
  );
};

const DetailedInformation = ({ info }: { info: BillingProperties }) => {
  const [infoOpen, setInfoOpen] = useState<boolean>(false);

  const closeHandler = () => setInfoOpen(false);
  const openHandler = () => setInfoOpen(true);

  return (
    <>
      <Info onClick={openHandler} />
      <CustomAlert
        open={infoOpen}
        onClose={closeHandler}
        title="Detailed Information"
        Content={({ onClose }) => <Content info={info} onClose={onClose} />}
      />
    </>
  );
};

const Content = ({
  info,
}: {
  info: BillingProperties;
  onClose: () => void;
}) => {
  return (
    <>
      <InfoRow>
        <Text title={"Client Name: "} size={"smallBold"} />
        <Text title={info.client.fullName} />
      </InfoRow>
      <InfoRow>
        <Text title={"Provider Name: "} size={"smallBold"} />
        <Text title={!!info.billedBy ? info.billedBy.fullName : "-"} />
      </InfoRow>
      <InfoRow>
        <Text title={"Session Type: "} size={"smallBold"} />
        <Text title={info.sessionType.name} />
      </InfoRow>
      <InfoRow>
        <Text title={"Session Date: "} size={"smallBold"} />
        <Text title={dayjs.utc(info.sessionDate).format("MM-DD-YYYY")} />
      </InfoRow>
      <InfoRow>
        <Text title={"Submit Date: "} size={"smallBold"} />
        <Text title={dayjs.utc(info.reportSubmittedAt).format("MM-DD-YYYY")} />
      </InfoRow>
      <InfoRow>
        <Text title={"Download Date: "} size={"smallBold"} />
        <Text
          title={
            !!info.reportExportedAt
              ? dayjs(info.reportExportedAt).format("MM-DD-YYYY")
              : "Not downloaded"
          }
        />
      </InfoRow>
      <InfoRow>
        <Text title={"Downloaded By: "} size={"smallBold"} />
        <Text
          title={!!info.reportExportedBy ? info.reportExportedBy.fullName : "-"}
        />
      </InfoRow>
      <InfoRow>
        <Text title={"MBH Billing Status: "} size={"smallBold"} />
        <Text title={typeof info.mbhBillingStatus === "number" ?
          MBHBillingStatus[info.mbhBillingStatus] :
          "Not Synced"
        } />
      </InfoRow>
      <InfoRow>
        <Text title={"Location: "} size={"smallBold"} />
        <Text title={!!info.location ? info.location.name : "-"} />
      </InfoRow>
      {!!info.sessionDateModifiedDetails ? (
        <>
          <Text title={"Modification Details: "} size={"mediumBold"} />
          <InfoRow>
            <Text title={"Modified By: "} size={"smallBold"} />
            <Text
              title={info.sessionDateModifiedDetails.dateModifiedBy.fullName}
            />
          </InfoRow>
          <InfoRow>
            <Text title={"Original Date: "} size={"smallBold"} />
            <Text
              title={dayjs(info.sessionDateModifiedDetails.originalDate).format(
                "MM-DD-YYYY"
              )}
            />
          </InfoRow>
          <InfoRow>
            <Text title={"Modified Date: "} size={"smallBold"} />
            <Text
              title={dayjs(
                info.sessionDateModifiedDetails.dateModifiedAt
              ).format("MM-DD-YYYY")}
            />
          </InfoRow>
        </>
      ) : null}
    </>
  );
};
